import { StrictMode, useState, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { router } from "./router";
import { ClerkProvider, useUser, useOrganization, useOrganizationList, useClerk, useAuth, useSignIn, useSignUp } from "@clerk/clerk-react";
import { CustomSignIn } from "./components/CustomSignIn";
import { CustomSignUp } from "./components/CustomSignUp";
import { CustomCreateOrg } from "./components/CustomCreateOrg";
import authStyles from "./components/Auth.module.css";
import { logger } from './log';
import "./styles/global.css";

const log = logger('main');

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error("Missing Clerk Publishable Key");
}

function AuthLoadingScreen() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'var(--bg-primary)',
    }} />
  );
}

function AuthRouter() {
  const [view, setView] = useState<'signin' | 'signup'>('signin');

  // Listen to hash changes
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === '#/sign-up') {
        setView('signup');
      } else {
        setView('signin');
      }
    };

    handleHashChange(); // Set initial view
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const goToSignUp = () => {
    window.location.hash = '#/sign-up';
    setView('signup');
  };

  const goToSignIn = () => {
    window.location.hash = '#/sign-in';
    setView('signin');
  };

  if (view === 'signup') {
    return <CustomSignUp onSignInClick={goToSignIn} />;
  }

  return <CustomSignIn onSignUpClick={goToSignUp} />;
}

function clearTicketParams() {
  const url = new URL(window.location.href);
  url.searchParams.delete('__clerk_ticket');
  url.searchParams.delete('__clerk_status');
  window.history.replaceState({}, '', url.toString());
}

function InviteTicketSignIn({ ticket }: { ticket: string }) {
  const { signIn, setActive } = useSignIn();
  const { signOut } = useClerk();
  const { isSignedIn } = useAuth();
  const [error, setError] = useState('');
  const [signingOut, setSigningOut] = useState(false);

  // If already signed in (impersonation flow), sign out first
  useEffect(() => {
    if (isSignedIn && !signingOut) {
      setSigningOut(true);
      signOut().catch(() => setSigningOut(false));
    }
  }, [isSignedIn, signingOut, signOut]);

  useEffect(() => {
    if (!signIn || isSignedIn) return;

    signIn.create({ strategy: 'ticket', ticket })
      .then(async (result) => {
        if (result.status === 'complete') {
          clearTicketParams();
          await setActive({ session: result.createdSessionId });
        } else {
          log.warn('ticket sign-in incomplete', { status: result.status });
          setError('Unable to process invitation. Please sign in manually.');
          clearTicketParams();
        }
      })
      .catch((err: any) => {
        log.error('ticket sign-in failed', err);
        const code = err.errors?.[0]?.code;
        if (code === 'organization_invitation_to_deleted_organization') {
          setError('This organization no longer exists. The invitation is no longer valid.');
        } else {
          setError(err.errors?.[0]?.longMessage || 'Invitation link is invalid or expired.');
        }
        clearTicketParams();
      });
  }, [signIn, setActive, ticket, isSignedIn]);

  if (error) {
    return (
      <div className={authStyles.container}>
        <div className={authStyles.card}>
          <div className={authStyles.header}>
            <div className={authStyles.eyebrow}>Interlucent</div>
            <h1 className={authStyles.title}>INVITATION ERROR</h1>
            <p className={authStyles.subtitle}>{error}</p>
          </div>
          <button onClick={() => window.location.replace('/')} className={authStyles.button}>
            GO TO SIGN IN
          </button>
        </div>
      </div>
    );
  }

  return <AuthLoadingScreen />;
}

function InviteTicketSignUp({ ticket }: { ticket: string }) {
  const { signUp, setActive } = useSignUp();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUp) return;

    setLoading(true);
    setError('');

    try {
      const result = await signUp.create({
        strategy: 'ticket',
        ticket,
        firstName,
        lastName,
        password,
      });

      if (result.status === 'complete') {
        clearTicketParams();
        await setActive({ session: result.createdSessionId });
      } else if (result.status === 'missing_requirements') {
        // May need email verification
        if (result.unverifiedFields?.includes('email_address')) {
          await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
          setPendingVerification(true);
        }
      }
    } catch (err: any) {
      log.error('ticket sign-up failed', err);
      const code = err.errors?.[0]?.code;
      if (code === 'organization_invitation_to_deleted_organization') {
        setError('This organization no longer exists. The invitation is no longer valid.');
      } else {
        setError(err.errors?.[0]?.longMessage || 'Failed to create account with invitation.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUp) return;

    setLoading(true);
    setError('');

    try {
      const result = await signUp.attemptEmailAddressVerification({ code: verificationCode });
      if (result.status === 'complete') {
        clearTicketParams();
        await setActive({ session: result.createdSessionId });
      }
    } catch (err: any) {
      setError(err.errors?.[0]?.longMessage || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  if (pendingVerification) {
    return (
      <div className={authStyles.container}>
        <div className={authStyles.card}>
          <div className={authStyles.header}>
            <div className={authStyles.eyebrow}>Interlucent</div>
            <h1 className={authStyles.title}>VERIFY EMAIL</h1>
            <p className={authStyles.subtitle}>
              Enter the verification code sent to your email
            </p>
          </div>

          <form onSubmit={handleVerify} className={authStyles.form}>
            {error && <div className={authStyles.error}>{error}</div>}

            <div className={authStyles.field}>
              <label className={authStyles.label}>VERIFICATION CODE</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                className={authStyles.input}
                placeholder="Enter 6-digit code"
                required
                autoFocus
                autoComplete="one-time-code"
              />
            </div>

            <button
              type="submit"
              disabled={loading || verificationCode.length < 6}
              className={authStyles.button}
            >
              {loading ? 'VERIFYING...' : 'VERIFY'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={authStyles.container}>
      <div className={authStyles.card}>
        <div className={authStyles.header}>
          <div className={authStyles.eyebrow}>Interlucent</div>
          <h1 className={authStyles.title}>ACCEPT INVITATION</h1>
          <p className={authStyles.subtitle}>
            Complete your account to join the organization
          </p>
        </div>

        <form onSubmit={handleSubmit} className={authStyles.form}>
          {error && <div className={authStyles.error}>{error}</div>}

          <div className={authStyles.fieldRow}>
            <div className={authStyles.field}>
              <label className={authStyles.label}>FIRST NAME</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={authStyles.input}
                required
                autoFocus
                autoComplete="given-name"
              />
            </div>

            <div className={authStyles.field}>
              <label className={authStyles.label}>LAST NAME</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={authStyles.input}
                required
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className={authStyles.field}>
            <label className={authStyles.label}>PASSWORD</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={authStyles.input}
              required
              minLength={8}
              autoComplete="new-password"
            />
            <p className={authStyles.hint}>Minimum 8 characters</p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={authStyles.button}
          >
            {loading ? 'CREATING ACCOUNT...' : 'CREATE ACCOUNT & JOIN'}
          </button>
        </form>
      </div>
    </div>
  );
}

function AuthWrapper() {
  const { user, isLoaded: userLoaded } = useUser();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const clerk = useClerk();
  const [processingInvite, setProcessingInvite] = useState(false);
  const [pendingInviteToConfirm, setPendingInviteToConfirm] = useState<any>(null);
  // Memberships and pending invitations straight from Clerk, each with its own
  // loading/error state. The create-org screen is only reachable once BOTH have
  // loaded and are both empty: the old gate read a hand-rolled invites array
  // ([] meant both "none" and "not fetched yet", fetch errors were swallowed)
  // and the cached user.organizationMemberships snapshot, so a user with an
  // existing org or invite could land on create-org and make a same-name
  // duplicate (prod: 2 extra "Cvent" orgs, 1 extra "PM Group", 2026-10-21).
  const { isLoaded: orgListLoaded, userMemberships, userInvitations } = useOrganizationList({
    userMemberships: { infinite: true },
    userInvitations: { status: 'pending', infinite: true },
  });
  const memberships = userMemberships?.data ?? [];
  const invites = userInvitations?.data ?? [];
  const orgListPending = !orgListLoaded || userMemberships.isLoading || userInvitations.isLoading;
  const orgListError = userMemberships?.error ?? userInvitations?.error ?? null;

  // TODO: Onboarding check will be done via WebSocket once connection is established
  // For now, we skip the initial onboarding check and let users proceed

  // Auto-select single organization (moved from render to useEffect to avoid side effects during render)
  useEffect(() => {
    if (!organization && user && !orgListPending && !processingInvite && !pendingInviteToConfirm) {
      if (memberships.length === 1 && invites.length === 0) {
        clerk.setActive({ organization: memberships[0].organization.id });
      }
    }
  }, [organization, user, orgListPending, processingInvite, pendingInviteToConfirm, memberships, invites.length, clerk]);

  // Accept invite handler
  const acceptInvite = async (invite: any) => {
    setProcessingInvite(true);
    setPendingInviteToConfirm(null);

    try {
      await invite.accept();
      await clerk.setActive({ organization: invite.publicOrganizationData.id });
    } catch (err: any) {
      log.error('failed to accept invitation', err);
      setProcessingInvite(false);
    }
  };

  // Wait for both user and org data to load
  if (!userLoaded || !orgLoaded) {
    return <AuthLoadingScreen />;
  }

  // Show confirmation dialog for pending invite
  if (pendingInviteToConfirm) {
    return (
      <div className={authStyles.container}>
        <div className={authStyles.card}>
          <div className={authStyles.header}>
            <div className={authStyles.eyebrow}>Interlucent</div>
            <h1 className={authStyles.title}>ORGANIZATION INVITATION</h1>
            <p className={authStyles.subtitle}>
              You've been invited to join <strong>{pendingInviteToConfirm.publicOrganizationData.name}</strong>
            </p>
          </div>

          <div className={authStyles.buttonRow}>
            <button
              className={`${authStyles.button} ${authStyles.secondaryButton}`}
              onClick={() => setPendingInviteToConfirm(null)}
            >
              CANCEL
            </button>
            <button
              className={authStyles.button}
              onClick={() => acceptInvite(pendingInviteToConfirm)}
            >
              ACCEPT & JOIN
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show accepting state if processing invite
  if (processingInvite) {
    return <AuthLoadingScreen />;
  }

  // If user doesn't have an organization, force them to create one
  if (!organization) {
    // Never decide "no organizations" before Clerk has answered.
    if (orgListPending) {
      return <AuthLoadingScreen />;
    }
    // A failed lookup is not "no organizations": offer a retry, not create-org.
    if (orgListError) {
      log.error('failed to load organizations or invitations', orgListError);
      return (
        <div className={authStyles.container}>
          <div className={authStyles.card}>
            <div className={authStyles.header}>
              <div className={authStyles.eyebrow}>Interlucent</div>
              <h1 className={authStyles.title}>COULDN'T LOAD ORGANIZATIONS</h1>
              <p className={authStyles.subtitle}>
                We couldn't check your organizations and invitations. Try again in a moment.
              </p>
            </div>
            <div className={authStyles.buttonRow}>
              <button
                className={authStyles.button}
                onClick={() => { userMemberships.revalidate?.(); userInvitations.revalidate?.(); }}
              >
                TRY AGAIN
              </button>
            </div>
          </div>
        </div>
      );
    }

    const hasMembershipsOrInvites = memberships.length > 0 || invites.length > 0;

    // Single membership auto-selection is handled by useEffect above
    if (memberships.length === 1 && invites.length === 0) {
      return <AuthLoadingScreen />;
    }

    // If user has existing organizations or invites, let them select one
    if (hasMembershipsOrInvites) {
      return (
        <div className={authStyles.container}>
          <div className={authStyles.card}>
            <div className={authStyles.header}>
              <div className={authStyles.eyebrow}>Interlucent</div>
              <h1 className={authStyles.title}>SELECT ORGANIZATION</h1>
              <p className={authStyles.subtitle}>
                Choose an organization to continue
              </p>
            </div>

            <div className={authStyles.selectList}>
              {memberships.map((membership: any) => (
                <button
                  key={membership.organization.id}
                  className={authStyles.selectItem}
                  onClick={() => clerk.setActive({ organization: membership.organization.id })}
                >
                  <span>{membership.organization.name}</span>
                  <span className={authStyles.badge}>Member</span>
                </button>
              ))}

              {invites.map((invite: any) => (
                <button
                  key={invite.id}
                  className={authStyles.selectItem}
                  onClick={() => setPendingInviteToConfirm(invite)}
                >
                  <span>{invite.publicOrganizationData.name}</span>
                  <span className={authStyles.badge}>Invite</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // No organizations exist - force user to create one
    return <CustomCreateOrg />;
  }

  // User has an active organization - show the app
  return <RouterProvider router={router} />;
}

// =============================================================================
// AuthenticatedApp — owns Clerk hooks. Only mounted for non-public routes so
// token-refresh re-renders never cascade to public pages like /preview.
// =============================================================================
function AuthenticatedApp() {
  const { isLoaded, isSignedIn } = useUser();
  // Detect pending session tasks (e.g. reset-password) — Clerk treats pending as signed-out by default
  const { sessionId: pendingSessionId } = useAuth({ treatPendingAsSignedOut: false });
  const { isSignedIn: isFullySignedIn } = useAuth();
  const hasPendingTask = !!pendingSessionId && !isFullySignedIn;

  if (!isLoaded) {
    return <AuthLoadingScreen />;
  }

  // User authenticated but has a pending session task (e.g. reset-password)
  // Redirect to the task page so Clerk's TaskResetPassword component can handle it
  if (hasPendingTask) {
    if (window.location.pathname !== '/reset-password-task') {
      window.location.href = '/reset-password-task';
      return null;
    }
    return <RouterProvider router={router} />;
  }

  // Handle ticket links (?__clerk_ticket=XXX&__clerk_status=sign_in|sign_up)
  // Check BEFORE auth state — impersonation tickets arrive when admin is already signed in
  const searchParams = new URLSearchParams(window.location.search);
  const clerkTicket = searchParams.get('__clerk_ticket');
  const clerkStatus = searchParams.get('__clerk_status');
  const pathname = window.location.pathname;

  if (clerkTicket) {
    if (clerkStatus === 'sign_up' || pathname === '/signup') {
      return <InviteTicketSignUp ticket={clerkTicket} />;
    }
    // Default to sign_in for invites and impersonation (no __clerk_status)
    return <InviteTicketSignIn ticket={clerkTicket} />;
  }

  if (!isSignedIn) {
    // /signin and /signup without tickets → show auth UI
    return <AuthRouter />;
  }

  // Signed-in user landed on /signin or /signup (stale bookmark) → redirect to app
  if (pathname === '/signin' || pathname === '/signup') {
    window.location.replace('/');
    return null;
  }

  return <AuthWrapper />;
}

// =============================================================================
// App — no Clerk hooks. Public routes render the router directly so Clerk
// token refreshes never trigger re-renders in the public component tree.
// =============================================================================
function App() {
  const isPublicRoute =
    window.location.pathname.startsWith('/preview') ||
    window.location.pathname.startsWith('/check') ||
    window.location.pathname === '/terms' ||
    window.location.pathname === '/eula' ||
    window.location.pathname === '/privacy' ||
    window.location.pathname === '/sso-callback' ||
    window.location.pathname === '/network-connectivity-requirements';

  if (isPublicRoute) {
    return <RouterProvider router={router} />;
  }

  return <AuthenticatedApp />;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      afterSignOutUrl="/"
      taskUrls={{ "reset-password": "/reset-password-task" }}
    >
      <App />
    </ClerkProvider>
  </StrictMode>
);
